<!--
  StoreDecorations.vue -- Store Decorations page, C1 direction (ADR 0064,
  amends ADR 0056). Pebble 3a: the SCAFFOLD.

  ADR 0056 shipped this tab as a READ-ONLY, decoration-first readiness
  rollup: a readiness banner, a needs-attention group, an approved group, a
  "How decorations work" explainer, and every row ending in a link out.
  ADR 0064 retires the "read + signpost, never edit" clause and rebuilds the
  page into the C1 shell -- a work strip, an "Add decoration" primary action,
  type-down search, a rail, and three collapsible sections whose rows expand
  one-at-a-time (accordion) into a detail region.

  THIS FILE IS THE 3a SCAFFOLD. It wires the shell: work strip + chips,
  search, rail, three sections with collapse, the accordion open/close state,
  and the section contract (updateCampaign/validateTab/loadComponent) the page
  now needs because it will gain write paths. The expanded detail is a
  PLACEHOLDER region -- the on-garment preview (3b), the row approval actions
  (3c), and bulk-apply "Apply to more products" (3d) fill it in later pebbles.
  Search for "SEAM (3" below to find every slot they hook into.

  KEY BEHAVIORS PRESERVED (handoff README "Key behaviors to preserve"):
  - The SHELL owns the page head, not this panel. The eyebrow (CATALOG),
    title (Decorations) and lede live on StoreEditShell.vue's `decorations`
    section entry and render once via EditorSectionHeader. This panel renders
    NO heading of its own -- that is the duplicate-heading bug fixed
    2026-07-21. The new lede is a change to that shell entry's `description`
    string, NOT panel markup.
  - The decoration-first deduped lens: every decoration this store uses,
    once. Data comes from the existing rollup store (no new endpoint); the
    backend already dedupes and now returns `zone_class` per placement (P1).
  - All 8 underscore-keyed lifecycle states unchanged; `archived` excluded.
  - The fetch-error state (distinct from empty) is retained.
  RETIRED: the readiness banner, the "How decorations work" explainer, and
  the read-only empty-state copy (Locked Decision 25 -- empty state is now an
  action).

  Backend: GET /api/stores/<store_id>/decoration_rollup/ via
  useDecorationRollupStore. The partition + search + work-strip counting live
  in ./decorationSections.js (pure, unit-tested) so the strip, the section
  headers, and the rail can never disagree about what needs the operator.

  SUPPLIER-SKU SEARCH: the rollup returns a per-placement `supplier_sku`
  (cp.product_item_number), which matchesQuery reads alongside product_name
  and zone. Search the placeholder advertises -- decorations, product names,
  SKUs -- is fully live.
-->
<template>
  <div class="sd" data-testid="store-decorations">
    <!-- NO page heading here: the shell (EditorSectionHeader) owns
         eyebrow/title/lede for this section. -->

    <div v-if="loading && !hasFetched" class="sd__loading" data-testid="decorations-loading">
      Loading decorations...
    </div>

    <!-- Fetch-error state -- MUST be checked before the empty state. A failed
         fetch also leaves `decorations` as [], so without this branch a
         failure renders identically to a genuinely-empty store. -->
    <div v-else-if="hasFetchError" class="sd-error" data-testid="decorations-error-state">
      <span class="tf-icon sd-error__icon">error_outline</span>
      <h2 class="sd-error__title">Couldn't load decorations -- try again</h2>
      <p class="sd-error__body">
        We weren't able to fetch this store's decorations. This is different
        from having zero decorations -- retry, or check back shortly.
      </p>
      <button
        type="button"
        class="sd-error__retry"
        data-testid="decorations-error-retry"
        @click="loadRollup(storeId)"
      >
        <span class="tf-icon">refresh</span>
        Retry
      </button>
    </div>

    <template v-else-if="hasDecorations">
      <!-- Override / Send Reminder failure surface (SUB-REGION 3c) -- covers
           both actions, mirrors ApprovalDetailPanel.vue's action-error. -->
      <p
        v-if="awStore.actionError"
        class="sd-action-error"
        data-testid="decoration-action-error"
      >
        {{ awStore.actionError }}
      </p>

      <!-- Decorator picker -- WHO makes this store's goods. Above the
           decoration list because it is a store-level decision, not a
           per-decoration one. -->
      <store-decorator-picker
        :store-id="storeId"
      />

      <!-- Add decoration: the page's primary action, above the work strip. -->
      <div class="sd-actions" data-testid="decorations-actions">
        <button
          type="button"
          class="sd-add-btn"
          data-testid="decorations-add-btn"
          @click="onAddDecoration"
        >
          <span class="tf-icon">add</span>
          Add decoration
        </button>
        <!-- ADR 0036: batch-send the store's never-sent artworks. Owns its
             own confirmation and result; the page only re-reads the rollup. -->
        <send-pending-artworks
          :decorations="decorations"
          :client-id="campaign?.client_id ?? null"
          @sent="loadRollup(storeId)"
        />
      </div>

      <!-- Work strip -- replaces the readiness banner. Leads with what there
           is to DO; each category is a filter chip. Launch gate stays a LINK
           (ADR 0041 -- Review & Launch owns it), not a banner. -->
      <div class="sd-strip" data-testid="decorations-work-strip">
        <div class="sd-strip__head">
          <div class="sd-strip__headline">
            <span data-testid="work-strip-needs-count">{{ needsCount }}</span>
            decoration{{ needsCount === 1 ? "" : "s" }} need{{ needsCount === 1 ? "s" : "" }} you
          </div>
          <div class="sd-strip__secondary" data-testid="work-strip-secondary">
            {{ readyCount }} of {{ placedCount }} placed approved &amp; in use
            <template v-if="unplacedCount > 0"> &middot; {{ unplacedCount }} not placed yet</template>
          </div>
          <a
            class="sd-strip__launch"
            data-testid="decorations-review-launch-link"
            :href="reviewLaunchHref"
            @click.prevent="goToReviewLaunch"
          >
            <span class="tf-icon">rocket_launch</span>
            Review &amp; Launch
          </a>
        </div>
        <div class="sd-strip__chips">
          <button
            v-for="cat in activeWorkCategories"
            :key="cat.key"
            type="button"
            class="sd-chip"
            :class="{ 'sd-chip--on': activeFilter === cat.key }"
            :data-testid="`work-strip-chip-${cat.key}`"
            @click="toggleFilter(cat.key)"
          >
            <span class="tf-icon sd-chip__icon">{{ cat.icon }}</span>
            {{ cat.label }}
            <span class="sd-chip__count">{{ cat.count }}</span>
          </button>
        </div>
      </div>

      <!-- Type-down search over decoration name/file/method + each placed
           product's store-listing-name / SKU / zone. A hit inside a collapsed
           section force-opens it (handled by isSectionOpen). -->
      <div class="sd-search">
        <span class="tf-icon sd-search__icon">search</span>
        <input
          v-model="query"
          type="text"
          class="sd-search__input"
          data-testid="decorations-search-input"
          placeholder="Search decorations, product names or SKUs"
        />
        <span
          v-if="query"
          class="tf-icon sd-search__clear"
          data-testid="decorations-search-clear"
          @click="query = ''"
        >close</span>
      </div>

      <div class="sd-grid">
        <!-- Rail: navigation over the same partitioned list. -->
        <aside class="sd-rail" data-testid="decorations-rail">
          <div
            v-for="sec in sectionsWithRows"
            :key="`rail-${sec.key}`"
            class="sd-rail__section"
          >
            <div class="sd-rail__label">{{ sec.label }}</div>
            <button
              v-for="d in sec.rows"
              :key="`rail-item-${d.decoration_id}`"
              type="button"
              class="sd-rail__item"
              :class="{ 'sd-rail__item--active': openId === d.decoration_id }"
              :data-testid="`rail-item-${d.decoration_id}`"
              @click="openDecoration(d.decoration_id)"
            >
              {{ decorationName(d) }}
            </button>
          </div>
        </aside>

        <!-- List: three collapsible sections, accordion rows. -->
        <div class="sd-list" data-testid="decorations-list">
          <section
            v-for="sec in sectionsWithRows"
            :key="sec.key"
            class="sd-section"
            :class="`sd-section--${sec.tone}`"
            :data-testid="`decorations-section-${sec.key}`"
          >
            <button
              type="button"
              class="sd-section__bar"
              :data-testid="`section-toggle-${sec.key}`"
              @click="toggleSection(sec.key)"
            >
              <span
                class="tf-icon sd-section__caret"
                :class="{ 'sd-section__caret--closed': !isSectionOpen(sec.key) }"
              >expand_more</span>
              <span class="sd-section__title">{{ sec.label }}</span>
              <span class="sd-section__count" :class="`sd-section__count--${sec.tone}`">
                {{ sec.rows.length }}
              </span>
            </button>

            <div
              v-if="isSectionOpen(sec.key)"
              class="sd-section__body"
              :data-testid="`section-body-${sec.key}`"
            >
              <div
                v-for="d in sec.rows"
                :key="d.decoration_id"
                class="sd-row"
                :class="{ 'sd-row--open': openId === d.decoration_id }"
                data-testid="decoration-row"
              >
                <div
                  class="sd-row__head"
                  :data-testid="`decoration-row-open-${d.decoration_id}`"
                  @click="openDecoration(d.decoration_id)"
                >
                  <DecorationArtThumb
                    v-if="decorationThumbnailSrc(d)"
                    :src="decorationThumbnailSrc(d)"
                    :ink-hex="d.dominant_ink_hex"
                    :ink-mixed="d.dominant_ink_mixed === true"
                    :alt="decorationName(d)"
                    class="sd-row__thumb"
                    :data-testid="`decoration-row-thumb-${d.decoration_id}`"
                  />
                  <span
                    v-else
                    class="sd-row__thumb sd-row__thumb--placeholder"
                    data-testid="decoration-row-thumb-placeholder"
                  >{{ decorationInitial(d) }}</span>

                  <div class="sd-row__main">
                    <div class="sd-row__headline">
                      <span class="sd-row__name" data-testid="decoration-row-name">{{ decorationName(d) }}</span>
                      <span v-if="d.method" class="sd-row__method" data-testid="decoration-row-method">
                        {{ d.method.display_name }}
                      </span>
                      <lifecycle-chip :state="rollupStateOf(d)" size="sm" />
                    </div>
                    <div class="sd-row__sub">
                      {{ placementSummary(d) }}
                    </div>
                  </div>

                  <span
                    class="tf-icon sd-row__caret"
                    :class="{ 'sd-row__caret--open': openId === d.decoration_id }"
                  >expand_more</span>
                </div>

                <!-- Expanded detail. The three later pebbles fill it in as
                     clearly-delimited sub-regions so they compose rather than
                     conflict:
                       SEAM (3b): on-garment preview per placement, true
                         relative scale, verdict pill.
                       SUB-REGION (3c) below: per-state approval actions.
                       SEAM (3d): bulk-apply "Apply to more products".
                     All writes go through decoration_set_apply / the existing
                     approval_records endpoints -- NOT the campaign blob
                     (updateCampaign stays a near-no-op). -->
                <div
                  v-if="openId === d.decoration_id"
                  class="sd-detail"
                  :data-testid="`decoration-detail-${d.decoration_id}`"
                >
                  <!-- ============================================================
                       SUB-REGION (3b): on-garment placement previews.
                       Owned entirely by pebble 3b. Sits ABOVE the 3c actions
                       sub-region and does NOT touch it. One GarmentPreview per
                       placement, at true relative scale (ADR 0065), each with a
                       reused PlacementCompatibilityIndicator verdict pill.
                       ============================================================ -->
                  <div
                    class="sd-previews"
                    :data-testid="`decoration-previews-${d.decoration_id}`"
                  >
                    <div class="sd-previews__head">
                      <span class="sd-previews__title">On-garment previews</span>
                    </div>

                    <div
                      v-if="d.placements.length"
                      class="sd-previews__grid"
                      :data-testid="`decoration-previews-grid-${d.decoration_id}`"
                    >
                      <garment-preview
                        v-for="p in d.placements"
                        :key="p.placement_id"
                        :decoration="d"
                        :placement="p"
                        @preview-kind="onPreviewKind"
                      />
                    </div>
                    <p
                      v-else
                      class="sd-previews__empty"
                      :data-testid="`decoration-previews-empty-${d.decoration_id}`"
                    >
                      Not on a product yet -- place this decoration to preview it
                      on a garment.
                    </p>
                  </div>

                  <!-- ============================================================
                       SUB-REGION (3c): per-state approval actions.
                       Owned entirely by pebble 3c. Restores #2340's Override +
                       Send Reminder (same approval_records bypass/remind
                       endpoints via the approvalWorkspace store, same required-
                       reason ReasonPicker, same per-approval-record targeting,
                       same capability gate + post-action refresh) and adds the
                       other per-state actions (Locked Decision 7). Kept in its
                       own block + testid namespace so 3b's preview above and
                       3d's bulk-apply below compose around it.
                       ============================================================ -->
                  <div
                    class="sd-acts"
                    :data-testid="`decoration-actions-${d.decoration_id}`"
                  >
                    <!-- Verbatim change-request text (Locked Decision 9): the
                         client's actual words, not a summary. Populated from
                         the rollup's change_request_text (FU#1 Half B) and
                         rendered only when present. Plain-text interpolation
                         (no v-html) -- client-supplied text is never treated
                         as markup. -->
                    <blockquote
                      v-if="changeRequestText(d)"
                      class="sd-acts__quote"
                      :data-testid="`decoration-change-request-text`"
                    >
                      <span class="tf-icon sd-acts__quote-icon">format_quote</span>
                      <span class="sd-acts__quote-text">{{ changeRequestText(d) }}</span>
                    </blockquote>

                    <!--
                      ADR 0070 -- into the STORE mount of the design
                      page. State-INDEPENDENT, so it sits above the
                      per-state action row rather than inside one of
                      its branches: the question this link answers
                      ("is each size cleared for this store's client?")
                      has a live answer in every lifecycle state,
                      including approved, and gating it on a work state
                      would hide the design exactly when someone wants
                      to confirm it is fine.

                      This does NOT replace "Open in Library" below.
                      Both mounts exist and go to different places --
                      that IS the dual mount. The Library owns the
                      design account-wide (every size, every method,
                      EVERY CLIENT); this store can only answer for its
                      own one client.

                      Omitted rather than broken when the row carries
                      no artwork: a link to .../artwork/undefined/design
                      renders a not-found page that reads as "this
                      design is missing" rather than "this row has no
                      design".
                    -->
                    <div v-if="d.artwork_id" class="sd-acts__row">
                      <a
                        class="sd-acts__link"
                        data-testid="decoration-open-design-link"
                        :href="storeDesignHref(d)"
                        @click.prevent="goToStoreDesign(d)"
                      >
                        <span class="tf-icon">design_services</span>
                        Open design in this store
                      </a>
                    </div>

                    <!-- Action buttons per lifecycle state. -->
                    <div class="sd-acts__row">
                      <!-- draft -> Submit for digitization + Open in Library -->
                      <template v-if="rollupStateOf(d) === 'draft'">
                        <button
                          type="button"
                          class="sd-acts__btn sd-acts__btn--primary"
                          data-testid="decoration-submit-digitization-btn"
                          @click="onSubmitForDigitization(d)"
                        >
                          <span class="tf-icon">send</span>
                          Submit for digitization
                        </button>
                        <a
                          class="sd-acts__link"
                          data-testid="decoration-open-in-library-link"
                          :href="libraryHref(d)"
                          @click.prevent="goToLibrary(d)"
                        >
                          <span class="tf-icon">open_in_new</span>
                          Open in Library
                        </a>
                      </template>

                      <!-- awaiting_digitization / in_digitization -> Track in
                           Library (nothing to do). -->
                      <template
                        v-else-if="['awaiting_digitization', 'in_digitization'].includes(rollupStateOf(d))"
                      >
                        <a
                          class="sd-acts__link"
                          data-testid="decoration-track-in-library-link"
                          :href="libraryHref(d)"
                          @click.prevent="goToLibrary(d)"
                        >
                          <span class="tf-icon">open_in_new</span>
                          Track in Library
                        </a>
                      </template>

                      <!-- awaiting_client_approval -> Send reminder + Override
                           + View proof. Override/Reminder restored from #2340. -->
                      <template v-else-if="rollupStateOf(d) === 'awaiting_client_approval'">
                        <!-- Single-client fast path: exactly one pending
                             approval -> act on that record directly. -->
                        <template v-if="canWrite && singlePendingApproval(d)">
                          <button
                            type="button"
                            class="sd-acts__btn sd-acts__btn--primary"
                            data-testid="decoration-send-reminder-btn"
                            title="Notify the client that their approval is still needed."
                            :disabled="awStore.actionPending"
                            @click="sendReminder(singlePendingApproval(d).approval_record_id)"
                          >
                            <span class="tf-icon">notifications_active</span>
                            Send reminder
                          </button>
                          <button
                            type="button"
                            class="sd-acts__btn sd-acts__btn--warn"
                            data-testid="decoration-override-btn"
                            title="Approve on the client's behalf to unblock launch. A reason is recorded on the audit trail."
                            @click="openOverrideModal(d, singlePendingApproval(d))"
                          >
                            <span class="tf-icon">gavel</span>
                            Override
                          </button>
                        </template>
                        <a
                          class="sd-acts__link"
                          data-testid="decoration-view-proof-link"
                          :href="approvalWorkspaceHref()"
                          @click.prevent="goToApprovalWorkspace()"
                        >
                          <span class="tf-icon">visibility</span>
                          View proof
                        </a>
                      </template>

                      <!-- changes_requested -> Upload revision + Reply to
                           client + Override. -->
                      <template v-else-if="rollupStateOf(d) === 'changes_requested'">
                        <button
                          type="button"
                          class="sd-acts__btn sd-acts__btn--primary"
                          data-testid="decoration-upload-revision-btn"
                          @click="onUploadRevision(d)"
                        >
                          <span class="tf-icon">upload_file</span>
                          Upload revision
                        </button>
                        <a
                          class="sd-acts__link"
                          data-testid="decoration-reply-to-client-link"
                          :href="approvalWorkspaceHref()"
                          @click.prevent="goToApprovalWorkspace()"
                        >
                          <span class="tf-icon">reply</span>
                          Reply to client
                        </a>
                        <button
                          v-if="canWrite && singlePendingApproval(d)"
                          type="button"
                          class="sd-acts__btn sd-acts__btn--warn"
                          data-testid="decoration-override-btn"
                          title="Approve on the client's behalf to unblock launch. A reason is recorded on the audit trail."
                          @click="openOverrideModal(d, singlePendingApproval(d))"
                        >
                          <span class="tf-icon">gavel</span>
                          Override
                        </button>
                      </template>

                      <!-- approved / in_use -> no action; state detail only. -->
                      <template v-else>
                        <span
                          class="sd-acts__settled"
                          data-testid="decoration-no-action-note"
                        >
                          <span class="tf-icon">check_circle</span>
                          Approved and in use -- nothing to do here.
                        </span>
                      </template>
                    </div>

                    <!-- Rare multi-client case (ADR 0017 artwork reuse): more
                         than one client is awaiting approval on the SAME
                         decoration. Per-client rows, each targeting THAT
                         client's approval_record_id. Applies to both
                         awaiting_client_approval and changes_requested. -->
                    <div
                      v-if="canWrite && multiPendingApprovals(d)"
                      class="sd-acts__clients"
                      data-testid="decoration-pending-clients-list"
                    >
                      <div class="sd-acts__clients-label">Awaiting approval from multiple clients</div>
                      <div
                        v-for="entry in multiPendingApprovals(d)"
                        :key="entry.approval_record_id"
                        class="sd-acts__client-row"
                        data-testid="decoration-pending-client-row"
                      >
                        <span class="sd-acts__client-name">{{ entry.client_name }}</span>
                        <button
                          type="button"
                          class="sd-acts__btn sd-acts__btn--primary sd-acts__btn--sm"
                          data-testid="decoration-send-reminder-btn"
                          title="Notify the client that their approval is still needed."
                          :disabled="awStore.actionPending"
                          @click="sendReminder(entry.approval_record_id)"
                        >
                          <span class="tf-icon">notifications_active</span>
                          Send reminder
                        </button>
                        <button
                          type="button"
                          class="sd-acts__btn sd-acts__btn--warn sd-acts__btn--sm"
                          data-testid="decoration-override-btn"
                          title="Approve on the client's behalf to unblock launch. A reason is recorded on the audit trail."
                          @click="openOverrideModal(d, entry)"
                        >
                          <span class="tf-icon">gavel</span>
                          Override
                        </button>
                      </div>
                    </div>
                  </div>
                  <!-- end SUB-REGION (3c) -->

                  <!-- ============================================================
                       SEAM (3d): bulk-apply "Apply to more products".
                       Owned entirely by pebble 3d. Sits BELOW the 3c actions
                       and the 3b preview. Extends THIS decoration's coverage
                       across the store's products via the single-decoration
                       apply endpoint, which routes into the SAME
                       decoration_set_apply cascade the set path uses (no second
                       write path). Locked Decisions 17-20 / 17a-c.
                       ============================================================ -->
                  <apply-to-more-products
                    v-if="canWrite"
                    :store-id="storeId"
                    :decoration="d"
                    :can-write="canWrite"
                    :data-testid="`decoration-apply-more-${d.decoration_id}`"
                    @applied="onDecorationApplied"
                  />
                  <!-- end SEAM (3d) -->
                </div>
              </div>
            </div>
          </section>

          <div
            v-if="visibleDecorations.length === 0"
            class="sd-nomatch"
            data-testid="decorations-no-match"
          >
            Nothing matches<template v-if="query"> "{{ query }}"</template>.
          </div>
        </div>
      </div>

      <!-- Override confirmation modal (ReasonPicker) -- SUB-REGION (3c),
           restored from #2340. Shared by the single-client fast path and the
           rare multi-client list; only one override flow runs at a time. -->
      <div
        v-if="overrideModalOpen"
        class="sd-modal-backdrop"
        data-testid="decoration-override-reason-modal"
        @click.self="cancelOverrideModal"
      >
        <div class="sd-modal">
          <h3 class="sd-modal__title">Override approval</h3>
          <p class="sd-modal__intro">
            Approve this artwork on the client's behalf to unblock launch. A reason is
            required and recorded on the audit trail.
          </p>
          <ReasonPicker
            v-model="overrideReasonId"
            :reasons="awStore.bypassReasons"
            :notes="overrideNotes"
            :required="true"
            confirm-label="Override and approve"
            cancel-label="Cancel"
            @update:notes="overrideNotes = $event"
            @confirm="confirmOverride"
            @cancel="cancelOverrideModal"
          />
        </div>
      </div>
    </template>

    <!-- Empty state -- an ACTION, not the old read-only promise
         (ADR 0064 "Empty state", Locked Decision 25). -->
    <template v-else>
      <!-- The decorator is a store-level choice that can be made before any
           decoration exists, so the picker renders here too. -->
      <store-decorator-picker
        :store-id="storeId"
      />

      <div class="sd-empty" data-testid="decorations-empty-state">
        <span class="tf-icon sd-empty__icon">design_services</span>
        <h2 class="sd-empty__title">Add a decoration to this store</h2>
        <p class="sd-empty__body">
          Pick a decoration, choose the products (fit is checked per garment),
          and get it signed off.
        </p>
        <button
          type="button"
          class="sd-empty__btn"
          data-testid="decorations-empty-add-btn"
          @click="onAddDecoration"
        >
          <span class="tf-icon">add</span>
          Add decoration
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { useDecorationRollupStore } from "@/stores/decorationRollup";
import { useApprovalWorkspaceStore } from "@/stores/approvalWorkspace";
import { formatArtworkName } from "@/utils/formatArtworkName";
import { decorationRowImageSrc } from "@/utils/artworkPreviewState";
import ReasonPicker from "@/components/ReasonPicker.vue";
import GarmentPreview from "@/components/decoration/GarmentPreview.vue";
import DecorationArtThumb from "@/components/decoration/DecorationArtThumb.vue";
import ApplyToMoreProducts from "@/components/decoration/ApplyToMoreProducts.vue";
import LifecycleChip from "./components/DecorationLifecycleChip.vue";
import StoreDecoratorPicker from "./components/StoreDecoratorPicker.vue";
import SendPendingArtworks from "./components/SendPendingArtworks.vue";
import {
  SECTIONS,
  sectionOf,
  rollupState,
  matchesQuery,
  hasFitIssue,
} from "./decorationSections";

// Work-strip filter categories. Each is a cross-cutting REASON within the
// "needs you" section (a decoration can hit more than one, so the headline
// is the size of the section, never a sum of chips). `predicate` runs over a
// decoration; `count` is derived. Ordered as the handoff lists them.
// "Doesn't fit" (SEAM 3b) has no source yet -- hasFitIssue is a stub -- so it
// shows a 0 count and is hidden until 3b wires verdicts.
const WORK_CATEGORIES = [
  { key: "changes_requested", label: "Changes requested", icon: "error_outline" },
  { key: "awaiting_client_approval", label: "Awaiting client", icon: "pending_actions" },
  { key: "doesnt_fit", label: "Doesn't fit", icon: "photo_size_select_small" },
  { key: "not_digitized", label: "Not digitized", icon: "schedule" },
];

export default {
  name: "StoreDecorations",

  components: {
    DecorationArtThumb,
    "lifecycle-chip": LifecycleChip,
    LifecycleChip,
    ReasonPicker,
    "garment-preview": GarmentPreview,
    GarmentPreview,
    "apply-to-more-products": ApplyToMoreProducts,
    ApplyToMoreProducts,
    "store-decorator-picker": StoreDecoratorPicker,
    StoreDecoratorPicker,
    "send-pending-artworks": SendPendingArtworks,
    SendPendingArtworks,
  },

  props: {
    campaign: Object,
    // Received under the shared section-component contract (the parent passes
    // it uniformly to every sibling tab); unused by the 3a scaffold.
    editMode: Boolean,
  },

  data() {
    return {
      hasFetched: false,
      // Accordion: exactly one decoration open at a time (C1). null = none.
      openId: null,
      // Section collapse. "available" (Not on a product yet) starts COLLAPSED
      // per Locked Decision; the other two start open. A search hit inside a
      // collapsed section force-opens it (isSectionOpen), without mutating
      // this map -- so clearing the query restores the user's collapse state.
      collapsed: { available: true },
      // Active work-strip filter chip (null = no filter).
      activeFilter: null,
      query: "",
      // Override (bypass) confirmation modal state (SUB-REGION 3c, restored
      // from #2340). Shared by the single-pending fast path and the rare
      // multi-client list -- only one override flow runs at a time.
      overrideModalOpen: false,
      overrideRecordId: null,
      overrideReasonId: null,
      overrideNotes: "",
    };
  },

  computed: {
    storeId() {
      return this.campaign?._jv?.id ?? this.campaign?.id ?? null;
    },

    rollupStore() {
      return useDecorationRollupStore();
    },

    awStore() {
      return useApprovalWorkspaceStore();
    },

    // Capability gate for the write actions (Override / Send Reminder),
    // restored verbatim from #2340: mirrors ApprovalWorkspacePage.vue's
    // canWrite. Reads $distributorCapabilities off globalProperties. When the
    // injection isn't wired, default to assuming write -- the route guard
    // already enforced read and the backend enforces write at the endpoint
    // boundary.
    canWrite() {
      const caps = this.$root?.$?.appContext?.config?.globalProperties
        ?.$distributorCapabilities;
      if (Array.isArray(caps)) {
        return caps.includes("distributor.decoration_library.write");
      }
      return true;
    },

    loading() {
      return this.storeId != null && this.rollupStore.isLoading(this.storeId);
    },

    rollup() {
      if (this.storeId == null) return null;
      return this.rollupStore.rollupFor(this.storeId);
    },

    hasFetchError() {
      return this.storeId != null && !!this.rollupStore.errorFor(this.storeId);
    },

    // The decoration-first deduped lens. The backend dedupes + orders; this
    // normalizes placements to an array and excludes `archived` at the source
    // so no downstream consumer (counts, sections, rail) ever sees it.
    decorations() {
      const raw = Array.isArray(this.rollup?.decorations) ? this.rollup.decorations : [];
      return raw
        .filter((d) => d.lifecycle_state !== "archived")
        .map((d) => ({
          ...d,
          placements: Array.isArray(d.placements) ? d.placements : [],
        }));
    },

    hasDecorations() {
      return this.decorations.length > 0;
    },

    // Decorations passing the current work-strip filter AND the search query.
    // The single source both the sections and the rail read.
    visibleDecorations() {
      return this.decorations.filter(
        (d) => this.passesFilter(d) && matchesQuery(d, this.query, (x) => this.decorationName(x)),
      );
    },

    // Sections carrying their (filtered) rows, in the locked order, dropping
    // empty ones -- the rail and the list both map over this.
    sectionsWithRows() {
      return SECTIONS.map((sec) => ({
        ...sec,
        rows: this.visibleDecorations.filter((d) => sectionOf(d) === sec.key),
      })).filter((sec) => sec.rows.length > 0);
    },

    // ---- Work-strip counts (over the SAME partition as the sections) ----
    needsCount() {
      return this.decorations.filter((d) => sectionOf(d) === "needs").length;
    },
    readyCount() {
      return this.decorations.filter((d) => sectionOf(d) === "ready").length;
    },
    unplacedCount() {
      return this.decorations.filter((d) => sectionOf(d) === "available").length;
    },
    placedCount() {
      return this.needsCount + this.readyCount;
    },

    // Work categories with a non-zero count, in order -- the rendered chips.
    activeWorkCategories() {
      return WORK_CATEGORIES.map((cat) => ({
        ...cat,
        count: this.decorations.filter((d) => this.matchesCategory(d, cat.key)).length,
      })).filter((cat) => cat.count > 0);
    },

    reviewLaunchHref() {
      if (this.storeId == null) return "#";
      return `/distributor/stores/${this.storeId}/edit`;
    },
    libraryListHref() {
      return "/distributor/decoration-library";
    },
  },

  watch: {
    storeId: {
      immediate: true,
      handler(newId) {
        if (newId != null) this.loadRollup(newId);
      },
    },
  },

  methods: {
    // ---- Section-component contract (StoreAddEdit.vue autosave) ----
    // This page gains write paths (later pebbles), so it joins the
    // updateCampaign/validateTab/loadComponent contract it was deliberately
    // exempt from. Pattern copied from StoreBranding.vue: updateCampaign is a
    // near-no-op (decoration writes go through decoration_set_apply in later
    // pebbles, NOT the campaign blob), validateTab reports the tab is valid
    // (returns the `invalid` flag -- false = not invalid), loadComponent is a
    // no-op (the rollup loads via the storeId watch).
    updateCampaign(campaign) {
      return { ...(campaign || {}) };
    },
    validateTab() {
      // The parent reads this as the `invalid` flag. The 3a scaffold has no
      // form fields, so it is always valid -> not invalid -> false. (Matches
      // StoreBranding.vue's return; do NOT emit update-tab-invalid here or the
      // parent's handler re-enters validateTab in a loop.)
      return false;
    },
    loadComponent() {
      // no-op -- the rollup loads via the storeId watch.
    },

    async loadRollup(storeId) {
      try {
        await this.rollupStore.fetchRollup(storeId);
      } catch (err) {
        // Non-fatal: surface via console so /smoke browser-console checks
        // catch a regression, but don't crash the section.
        // eslint-disable-next-line no-console
        console.warn("Decorations rollup fetch failed", describeError(err));
      } finally {
        this.hasFetched = true;
      }
    },

    // ---- Bulk-apply "Apply to more products" (pebble 3d) ----
    // After a successful single-decoration apply, refresh the rollup so
    // the coverage bar, the placement list, and section membership (a
    // now-placed decoration may move out of "Not on a product yet")
    // reflect the write. The open row stays open (Follow-a-row-that-
    // moves, Locked Decision 17c) because openId is unchanged.
    async onDecorationApplied() {
      if (this.storeId != null) await this.loadRollup(this.storeId);
    },

    // ---- On-garment preview coverage logging (pebble 3b) ----
    // Each GarmentPreview reports the quality of the preview it rendered
    // (true / generic / no-dims / blocked). We log the ROUGH ones loudly so the
    // silhouette-hotspot coverage gap is visible in the browser console (and to
    // /smoke's console check), never silent -- Jake's locked best-effort rule.
    // The verdict pill on every preview stays EXACT regardless (arithmetic on
    // inches), so a rough drawing never means a wrong fit call.
    onPreviewKind(info) {
      if (!info || info.kind === "true") return;
      // eslint-disable-next-line no-console
      console.warn(
        `[Store Decorations] rough on-garment preview (${info.kind}): ` +
          `product="${info.product_name}" zone="${info.zone}" ` +
          `silhouette="${info.silhouette}". Verdict remains exact.`,
      );
    },

    // ---- Accordion / rail ----
    openDecoration(id) {
      // One open at a time: clicking the open row toggles it shut.
      this.openId = this.openId === id ? null : id;
    },

    // ---- Sections ----
    toggleSection(key) {
      this.collapsed = { ...this.collapsed, [key]: !this.collapsed[key] };
    },
    // A section is open when it isn't collapsed, OR a search is active and it
    // has a matching row (a query that matches something but shows nothing is
    // a bug, not a filter -- handoff "Sections and search").
    isSectionOpen(key) {
      if (this.query) {
        return this.sectionsWithRows.some((sec) => sec.key === key);
      }
      return !this.collapsed[key];
    },

    // ---- Work-strip filter ----
    toggleFilter(key) {
      this.activeFilter = this.activeFilter === key ? null : key;
    },
    passesFilter(d) {
      if (!this.activeFilter) return true;
      return this.matchesCategory(d, this.activeFilter);
    },
    // Whether a decoration belongs to a work-strip category.
    matchesCategory(d, key) {
      const state = this.rollupStateOf(d);
      const placed = (d.placements || []).length > 0;
      switch (key) {
        case "changes_requested":
          return placed && state === "changes_requested";
        case "awaiting_client_approval":
          return placed && state === "awaiting_client_approval";
        case "not_digitized":
          // "Not digitized" == draft / awaiting_digitization / in_digitization
          // on a placed decoration.
          return placed && ["draft", "awaiting_digitization", "in_digitization"].includes(state);
        case "doesnt_fit":
          // Wired in pebble 3b: a placed decoration with at least one
          // placement whose fit verdict is not `fit` (remap / drop). Shares the
          // exact predicate the sections partition uses (hasFitIssue), so the
          // chip count and the "needs you" membership cannot disagree.
          return placed && hasFitIssue(d);
        default:
          return false;
      }
    },

    // ---- Row rendering helpers ----
    // Worst-of rollup state, from the shared partition module (one item in
    // "Needs you" however many placements are mid-flight).
    rollupStateOf(d) {
      return rollupState(d);
    },
    decorationName(d) {
      // The editable name (artwork_filename) first (#703), then the
      // uploaded file's name.
      const raw = d.artwork_filename || d.artwork_original_filename;
      return formatArtworkName(raw) || `Decoration #${d.decoration_id}`;
    },
    // The shared tile-image rule: a stitch file's READY preview, else the
    // thumbnail / image_url -- never the raw .dst/.emb (a broken image).
    decorationThumbnailSrc(d) {
      return decorationRowImageSrc(d);
    },
    decorationInitial(d) {
      const first = String(this.decorationName(d) || "").trim().charAt(0);
      return first ? first.toUpperCase() : "D";
    },
    placementSummary(d) {
      const n = (d.placements || []).length;
      if (!n) return "Not on a product yet";
      return `On ${n} product${n === 1 ? "" : "s"} in this store`;
    },

    // ---- Add decoration ----
    // 3a stub: routes to the existing decoration library add flow. A richer
    // in-page add lands with later pebbles; this does NOT build a new
    // add-decoration backend.
    onAddDecoration() {
      this.$router.push(this.libraryListHref);
    },

    goToReviewLaunch() {
      this.$router.push(this.reviewLaunchHref);
    },

    // ---- SUB-REGION (3c): per-state approval actions ----
    //
    // Override + Send Reminder are RESTORED from #2340 unchanged: both reuse
    // the EXISTING approval_records bypass/remind endpoints via the shared
    // approvalWorkspace Pinia store (no new approval logic, no new backend),
    // the same required-reason ReasonPicker, the same per-approval-record
    // targeting, and the same post-action rollup refresh. The only change is
    // location: promoted from the row headline to the accordion detail region.

    // The client's change-request text, shown verbatim (Locked Decision 9).
    // The rollup returns ``change_request_text`` per decoration (FU#1 Half B):
    // the CLIENT's verbatim comment for the decoration's changes_requested
    // state, sourced from the request_changes transition on the backend. NULL
    // for awaiting_approval / approved / draft. Rendered as plain-text
    // interpolation in the template (no v-html) so client-supplied text can
    // never inject markup.
    changeRequestText(d) {
      const raw = d.change_request_text;
      const s = typeof raw === "string" ? raw.trim() : "";
      return s || null;
    },

    singlePendingApproval(d) {
      const pending = Array.isArray(d.pending_approvals) ? d.pending_approvals : [];
      return pending.length === 1 ? pending[0] : null;
    },

    multiPendingApprovals(d) {
      const pending = Array.isArray(d.pending_approvals) ? d.pending_approvals : [];
      return pending.length > 1 ? pending : null;
    },

    async openOverrideModal(_d, pendingEntry) {
      this.overrideRecordId = pendingEntry.approval_record_id;
      this.overrideReasonId = null;
      this.overrideNotes = "";
      this.overrideModalOpen = true;
      // Lazy-load reasons the first time the picker opens (mirrors
      // ApprovalDetailPanel.openReasonPicker).
      await this.awStore.fetchBypassReasons();
    },

    cancelOverrideModal() {
      this.overrideModalOpen = false;
      this.overrideRecordId = null;
      this.overrideReasonId = null;
      this.overrideNotes = "";
    },

    async confirmOverride() {
      if (this.overrideRecordId == null || this.overrideReasonId == null) return;
      try {
        await this.awStore.bypass(
          this.overrideRecordId,
          this.overrideReasonId,
          this.overrideNotes,
        );
        this.cancelOverrideModal();
        // Refresh the rollup so the pill/state reflects the now-approved
        // record (launch unblocks naturally via the existing predicate).
        if (this.storeId != null) await this.loadRollup(this.storeId);
      } catch (err) {
        // Surfaces via awStore.actionError; keep the modal open so the
        // distributor can retry or pick a different reason.
        // eslint-disable-next-line no-console
        console.warn("Decoration override (bypass) failed", describeError(err));
      }
    },

    async sendReminder(recordId) {
      try {
        await this.awStore.sendReminder(recordId);
        if (this.storeId != null) await this.loadRollup(this.storeId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Decoration send-reminder failed", describeError(err));
      }
    },

    // Non-approval per-state actions (Locked Decision 7). These route to the
    // existing surfaces that own the underlying flows -- pebble 3c wires the
    // buttons; it does NOT build new digitization / revision backends.
    onSubmitForDigitization(d) {
      // The library artwork detail owns the "submit for digitization"
      // lifecycle move; route there rather than duplicate that flow here.
      this.goToLibrary(d);
    },
    onUploadRevision(d) {
      // Revision upload is authored on the artwork detail in the library.
      this.goToLibrary(d);
    },

    // ---- Library / approval-workspace navigation (from #2340) ----
    libraryHref(d) {
      if (d.artwork_id) return `/distributor/decoration-library/artwork/${d.artwork_id}`;
      return this.libraryListHref;
    },

    // ---- ADR 0070: the design page's STORE mount ----
    // Same design page as the Library's, nested under this store, with
    // its approval column filtered to this store's one client. The
    // caller guards on `d.artwork_id`, so this never builds a path with
    // an undefined segment.
    storeDesignHref(d) {
      return (
        `/distributor/stores/${this.storeId}` +
        `/edit/decorations/artwork/${d.artwork_id}/design`
      );
    },
    goToStoreDesign(d) {
      this.$router.push(this.storeDesignHref(d));
    },
    goToLibrary(d) {
      this.$router.push(this.libraryHref(d));
    },
    // The workspace deep-link filters by store only (matching
    // StoreReviewLaunch.vue's convention). "View proof" and "Reply to client"
    // both land here -- the workspace holds the proof + thread.
    approvalWorkspaceHref() {
      const storeQuery = this.storeId != null ? `?filter=store:${this.storeId}` : "";
      return `/distributor/decorations/approvals${storeQuery}`;
    },
    goToApprovalWorkspace() {
      this.$router.push(this.approvalWorkspaceHref());
    },
  },
};
</script>

<style scoped>
.sd {
  font-family: "Roboto", sans-serif;
}
.sd__loading {
  color: var(--tf-fg-4, #757575);
  font-size: 14px;
  padding: 24px 0;
}

/* ── Add decoration ─────────────────────────────────────────────────── */
.sd-actions {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}
.sd-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: var(--tf-primary, #119ae9);
  color: var(--tf-bg, #fff);
}

/* ── Work strip ─────────────────────────────────────────────────────── */
.sd-strip {
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 18px;
}
.sd-strip__head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.sd-strip__headline {
  flex: 1;
  min-width: 0;
  font-family: "Poppins", sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
  line-height: 1.3;
}
.sd-strip__secondary {
  font-size: 12.5px;
  color: var(--tf-fg-4, #757575);
  flex-basis: 100%;
  margin-top: 3px;
  order: 3;
}
.sd-strip__launch {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tf-primary, #119ae9);
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}
.sd-strip__chips {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  margin-top: 12px;
}
.sd-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  padding: 4px 11px 4px 9px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid var(--tf-border, #e0e0e0);
  background: var(--tf-bg, #fff);
  color: var(--tf-fg-2, #424242);
}
.sd-chip--on {
  border-color: var(--tf-primary, #119ae9);
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary-dark, #0b6fa4);
}
.sd-chip__icon {
  font-size: 14px;
}
.sd-chip__count {
  font-weight: 700;
}

/* ── Search ─────────────────────────────────────────────────────────── */
.sd-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 9999px;
  padding: 7px 14px 7px 12px;
  margin-bottom: 16px;
  max-width: 460px;
}
.sd-search__icon {
  font-size: 18px;
  color: var(--tf-fg-4);
  flex-shrink: 0;
}
.sd-search__input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 0;
  font-family: inherit;
  font-size: 13.5px;
  color: var(--tf-fg-1, #212121);
  background: transparent;
}
.sd-search__clear {
  font-size: 17px;
  color: var(--tf-fg-4, #757575);
  cursor: pointer;
  flex-shrink: 0;
}

/* ── Grid: rail + list ──────────────────────────────────────────────── */
.sd-grid {
  display: grid;
  grid-template-columns: minmax(0, 220px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}
@media (max-width: 1040px) {
  .sd-grid {
    grid-template-columns: 1fr;
  }
  .sd-rail {
    display: none;
  }
}

/* ── Rail ───────────────────────────────────────────────────────────── */
.sd-rail {
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 12px;
  overflow: hidden;
  position: sticky;
  /* Below the editor's sticky pager row (StoreEditShell sets --ces-pager-h). */
  top: var(--ces-pager-h, 0px);
}
.sd-rail__section {
  padding: 6px 0;
  border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.sd-rail__section:last-child {
  border-bottom: none;
}
.sd-rail__label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #757575);
  padding: 6px 13px 5px;
}
.sd-rail__item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 12.5px;
  color: var(--tf-fg-2, #424242);
  padding: 6px 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-left: 3px solid transparent;
}
.sd-rail__item--active {
  border-left-color: var(--tf-primary, #119ae9);
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary-dark, #0b6fa4);
}

/* ── Sections ───────────────────────────────────────────────────────── */
.sd-list {
  min-width: 0;
}
.sd-section {
  margin-bottom: 22px;
}
.sd-section__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 0 11px;
  font-family: inherit;
}
.sd-section__caret {
  font-size: 18px;
  color: var(--tf-fg-4, #757575);
  transition: transform 0.12s ease;
}
.sd-section__caret--closed {
  transform: rotate(-90deg);
}
.sd-section__title {
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--tf-fg-2, #424242);
}
.sd-section__count {
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  padding: 1px 7px;
  border-radius: 9999px;
  background: var(--tf-chip-neutral-bg, #eef0f3);
  color: var(--tf-fg-3, #616161);
}
.sd-section__count--warn {
  background: var(--tf-warning-soft, #fff8e1);
  color: var(--tf-warning-dark, #8a6d3b);
}
.sd-section__count--ok {
  background: var(--tf-success-soft, #e6f7ed);
  color: var(--tf-success-dark, #2e7d32);
}
.sd-section__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── Rows ───────────────────────────────────────────────────────────── */
.sd-row {
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 10px;
  overflow: hidden;
}
.sd-row--open {
  border-color: var(--tf-primary, #119ae9);
}
.sd-row__head {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 13px 15px;
  cursor: pointer;
}
.sd-row__thumb {
  height: 40px;
  width: auto;
  max-width: 64px;
  border-radius: 6px;
  object-fit: contain;
  background: var(--tf-surface-muted, #fafbfc);
  border: 1px solid var(--tf-border, #e0e0e0);
  flex-shrink: 0;
}
.sd-row__thumb--placeholder {
  width: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: var(--tf-fg-4, #757575);
  font-family: "Poppins", sans-serif;
}
.sd-row__main {
  flex: 1;
  min-width: 0;
}
.sd-row__headline {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}
.sd-row__name {
  font-size: 14.5px;
  color: var(--tf-fg-1, #212121);
  font-weight: 500;
}
.sd-row__method {
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
}
.sd-row__sub {
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
  margin-top: 5px;
}
.sd-row__caret {
  font-size: 20px;
  color: var(--tf-fg-4, #757575);
  flex-shrink: 0;
  transition: transform 0.12s ease;
}
.sd-row__caret--open {
  transform: rotate(180deg);
}

/* ── Expanded detail ────────────────────────────────────────────────── */
.sd-detail {
  border-top: 1px solid var(--tf-border, #e0e0e0);
  background: var(--tf-surface-muted, #fafbfc);
  padding: 16px;
}

/* ── SUB-REGION (3b): on-garment previews ───────────────────────────── */
.sd-previews__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.sd-previews__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--tf-fg-3, #424242);
}
.sd-previews__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
}
.sd-previews__empty {
  font-size: 13px;
  margin: 0;
  color: var(--tf-fg-4, #757575);
  line-height: 1.5;
}

/* ── SUB-REGION (3c): per-state approval actions ────────────────────── */
.sd-acts {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--tf-border, #e0e0e0);
}
.sd-acts__quote {
  display: flex;
  gap: 8px;
  margin: 0 0 12px;
  padding: 10px 12px;
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-danger-border, #f3c8c8);
  border-left: 3px solid var(--tf-red, #ef0405);
  border-radius: 8px;
  font-size: 12.5px;
  color: var(--tf-fg-2, #424242);
  line-height: 1.55;
}
.sd-acts__quote-icon {
  font-size: 15px;
  color: var(--tf-red, #ef0405);
  flex-shrink: 0;
}
.sd-acts__quote-text {
  font-style: italic;
}
.sd-acts__row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.sd-acts__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 9999px;
  padding: 5px 13px;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid var(--tf-border, #e0e0e0);
  background: var(--tf-bg, #fff);
  color: var(--tf-fg-2, #424242);
}
.sd-acts__btn--primary {
  background: var(--tf-primary, #119ae9);
  border-color: var(--tf-primary, #119ae9);
  color: var(--tf-bg, #fff);
}
.sd-acts__btn--warn {
  background: var(--tf-warning-soft, #fff8e1);
  border-color: var(--tf-warning-dark, #8a6d3b);
  color: var(--tf-warning-dark, #8a6d3b);
}
.sd-acts__btn--warn:hover:not(:disabled) {
  background: var(--tf-warning, #fcb900);
}
.sd-acts__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.sd-acts__btn--sm {
  font-size: 11.5px;
  padding: 3px 10px;
}
.sd-acts__link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tf-primary, #119ae9);
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}
.sd-acts__link:hover {
  text-decoration: underline;
}
.sd-acts__settled {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--tf-success-dark, #2e7d32);
}
.sd-acts__settled .tf-icon {
  font-size: 16px;
}

/* Rare multi-client pending list (ADR 0017 artwork reuse). */
.sd-acts__clients {
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 8px;
}
.sd-acts__clients-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--tf-fg-3);
  margin-bottom: 8px;
}
.sd-acts__client-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
}
.sd-acts__client-row + .sd-acts__client-row {
  border-top: 1px solid var(--tf-border, #e0e0e0);
}
.sd-acts__client-name {
  flex: 1;
  font-size: 12.5px;
  color: var(--tf-fg-1, #212121);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Override / Send Reminder failure banner (mirrors ApprovalDetailPanel). */
.sd-action-error {
  background: var(--tf-danger-soft, #fdecec);
  border: 1px solid var(--tf-danger-border, #f3c8c8);
  color: var(--tf-red, #ef0405);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 14px;
}

/* Override confirmation modal (ReasonPicker), from #2340. */
.sd-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(19, 55, 82, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.sd-modal {
  background: var(--tf-bg, #fff);
  border-radius: 12px;
  padding: 22px 24px;
  width: 420px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
}
.sd-modal__title {
  font-family: "Poppins", sans-serif;
  font-size: 17px;
  font-weight: 500;
  color: var(--tf-fg-1, #212121);
  margin: 0 0 8px;
}
.sd-modal__intro {
  font-size: 13px;
  color: var(--tf-fg-3, #616161);
  line-height: 1.55;
  margin: 0 0 14px;
}

/* ── No-match ───────────────────────────────────────────────────────── */
.sd-nomatch {
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 12px;
  padding: 34px 28px;
  text-align: center;
  color: var(--tf-fg-3, #616161);
  font-size: 14px;
}

/* ── Fetch-error state ──────────────────────────────────────────────── */
.sd-error {
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-danger-border, #f3c8c8);
  border-radius: 14px;
  padding: 40px 40px 36px;
  text-align: center;
}
.sd-error__icon {
  font-size: 32px;
  color: var(--tf-red, #ef0405);
}
.sd-error__title {
  font-family: "Poppins", sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: var(--tf-fg-1, #212121);
  margin: 10px 0 8px;
}
.sd-error__body {
  font-size: 14px;
  color: var(--tf-fg-3, #616161);
  line-height: 1.6;
  margin: 0 auto 22px;
  max-width: 460px;
}
.sd-error__retry {
  border-radius: 9999px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  background: var(--tf-primary, #119ae9);
  color: var(--tf-bg, #fff);
}

/* ── Empty state (action, not read-only copy) ───────────────────────── */
.sd-empty {
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 14px;
  padding: 44px 40px 40px;
  text-align: center;
}
.sd-empty__icon {
  font-size: 34px;
  color: var(--tf-primary, #119ae9);
}
.sd-empty__title {
  font-family: "Poppins", sans-serif;
  font-size: 22px;
  font-weight: 500;
  color: var(--tf-secondary, #133752);
  margin: 10px 0 10px;
}
.sd-empty__body {
  font-size: 14px;
  color: var(--tf-fg-3, #616161);
  line-height: 1.6;
  margin: 0 auto 22px;
  max-width: 460px;
}
.sd-empty__btn {
  border-radius: 9999px;
  padding: 11px 24px;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  background: var(--tf-primary, #119ae9);
  color: var(--tf-bg, #fff);
}
</style>
